/**
 * @file routing.js
 * @description 브라우저 경로 문자열을 페이지 라우트 정보로 매핑하는 유틸리티입니다.
 * @used-in hooks/useAppRouter.js, hooks/useHeaderState.js
 */

import { pageMeta } from '../constants/navigation';

export function normalizePath(pathname) {
  // 기능 목적: 쿼리/해시를 제거하고 마지막 슬래시를 정리해 비교 기준 경로를 만듭니다.
  if (!pathname) return '/';
  const sanitized = pathname.split('?')[0].split('#')[0];
  return sanitized === '' ? '/' : sanitized.replace(/\/+$/, '') || '/';
}

export function getRoute(pathname) {
  const normalized = normalizePath(pathname);
  // 기능 목적: 뉴스 상세의 slug를 동적 경로에서 먼저 추출합니다.
  const newsMatch = normalized.match(/^\/news\/([^/]+)\.html$/);

  if (normalized === '/') return { page: 'home' };
  if (normalized === '/company/ceo.html') return { page: 'companyCeo', meta: pageMeta.companyCeo };
  if (normalized === '/company/about.html') return { page: 'companyAbout', meta: pageMeta.companyAbout };
  if (normalized === '/company/history.html') return { page: 'companyHistory', meta: pageMeta.companyHistory };
  // [비즈니스] 관리자 전용 연혁 편집 진입 경로 — 일반 메뉴에는 노출되지 않습니다.
  if (normalized === '/company/history_edit') return { page: 'companyHistory', adminTrigger: true, meta: pageMeta.companyHistory };
  if (normalized === '/company/customers.html') return { page: 'companyCustomers', meta: pageMeta.companyCustomers };
  if (normalized === '/company/maintenance.html') return { page: 'companyMaintenance', meta: pageMeta.companyMaintenance };
  if (normalized === '/solution/withgmp.html')   return { page: 'solution', meta: pageMeta.solution, activeTab: 'withgmp' };
  if (normalized === '/solution/witheai.html')   return { page: 'solution', meta: pageMeta.solution, activeTab: 'witheai' };
  if (normalized === '/solution/cmms.html')      return { page: 'solution', meta: pageMeta.solution, activeTab: 'cmms' };
  if (normalized === '/solution/fems.html')      return { page: 'solution', meta: pageMeta.solution, activeTab: 'fems' };
  if (normalized === '/solution/withaigmp.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'withaigmp' };
  if (normalized === '/solution/rws.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'rws' };
  if (normalized === '/solution/mes.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'mes' };
  if (normalized === '/solution/wms.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'wms' };
  // [비즈니스] 구 solution/maintenance URL은 company/maintenance 페이지로 리다이렉트합니다.
  if (normalized === '/solution/maintenance.html') return { page: 'companyMaintenance', meta: pageMeta.companyMaintenance };
  if (normalized === '/news/index.html') return { page: 'newsList', meta: pageMeta.newsList };
  // [비즈니스] 관리자 전용 진입 경로 — 일반 메뉴에는 노출되지 않습니다.
  if (normalized === '/news/news_edit') return { page: 'newsList', adminTrigger: true, meta: pageMeta.newsList };
  if (newsMatch) return { page: 'newsDetail', meta: pageMeta.newsDetail, slug: newsMatch[1] };
  if (normalized === '/cs/faq.html') return { page: 'faq', meta: pageMeta.faq };
  // [비즈니스] 관리자 전용 FAQ 편집 진입 경로 — 일반 메뉴에는 노출되지 않습니다.
  if (normalized === '/cs/faq_edit') return { page: 'faq', adminTrigger: true, meta: pageMeta.faq };
  if (normalized === '/contact' || normalized === '/cs/contact.html' || normalized === '/inquiry') {
    return { page: 'contact', meta: pageMeta.contact };
  }
  return { page: 'notFound', meta: pageMeta.notFound };
}

export function getTopLevelNavKey(pathname) {
  const normalized = normalizePath(pathname);
  // 기능 목적: 상단 GNB 활성 상태를 1차 경로 기준으로 계산합니다.
  if (normalized === '/') return 'home';
  if (normalized.startsWith('/company/')) return 'company';
  if (normalized.startsWith('/solution/')) return 'solution';
  if (normalized.startsWith('/news/')) return 'news';
  if (normalized === '/contact' || normalized === '/inquiry' || normalized.startsWith('/cs/')) return 'cs';
  return '';
}

export function getSubNavKey(pathname) {
  const normalized = normalizePath(pathname);
  // 기능 목적: 하위 메뉴 active 상태를 상세 경로와 1:1로 매핑합니다.
  if (normalized === '/company/about.html') return 'about';
  if (normalized === '/company/ceo.html') return 'ceo';
  if (normalized === '/company/history.html') return 'history';
  if (normalized === '/company/customers.html') return 'customers';
  if (normalized === '/company/maintenance.html') return 'maintenance';
  if (normalized === '/solution/withgmp.html')   return 'withgmp';
  if (normalized === '/solution/witheai.html')   return 'witheai';
  if (normalized === '/solution/cmms.html')      return 'cmms';
  if (normalized === '/solution/fems.html')      return 'fems';
  if (normalized === '/solution/withaigmp.html') return 'withaigmp';
  if (normalized === '/solution/rws.html') return 'rws';
  if (normalized === '/solution/mes.html') return 'mes';
  if (normalized === '/solution/wms.html') return 'wms';
  if (normalized === '/solution/maintenance.html') return 'maintenance';
  if (normalized === '/cs/faq.html') return 'faq';
  if (normalized === '/contact' || normalized === '/cs/contact.html' || normalized === '/inquiry') return 'contact';
  return '';
}
